import type { TFunction } from 'i18next';
import type {
  AssessedArea,
  RiskArea,
  DimensionSummary,
  HmsRiskLevel,
  Placement,
  RiskActivity,
  RiskResult,
  WorkEnvironmentRisk,
  WorkEnvironmentSurvey,
} from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';
import { joinList } from '../HRDashboard/hmsFormat';
import { currentStepIndex, dayAndMonth, PROCESS_STEPS } from './surveyProcessView';
import { riskLevel } from '../HRDashboard/HRRiskMatrix/riskMatrixView';

const LEVELS = [1, 2, 3, 4, 5];

/** The page shows the risk from when the survey closes until the team starts proposing measures */
const RISK_STATUSES: WorkEnvironmentSurveyStatus[] = [
  WorkEnvironmentSurveyStatus.AWAITING_RISK_CALC,
  WorkEnvironmentSurveyStatus.RISK_CALCULATED,
  WorkEnvironmentSurveyStatus.RISK_ASSESSED,
  WorkEnvironmentSurveyStatus.INSUFFICIENT_RESPONSES,
];

type SurveyState = Pick<WorkEnvironmentSurvey, 'status' | 'riskCalculatedAt' | 'actionPlanOpenedAt'>;

/** A survey archived because too few answered ends at the risk step, and is shown there */
const archivedAtRiskStep = (survey: SurveyState) =>
  survey.status === WorkEnvironmentSurveyStatus.ARCHIVED &&
  Boolean(survey.riskCalculatedAt) &&
  !survey.actionPlanOpenedAt;

export const showsRiskPage = (survey: SurveyState) =>
  RISK_STATUSES.includes(survey.status) || archivedAtRiskStep(survey);

export const processStepIndex = (survey: SurveyState) =>
  archivedAtRiskStep(survey) ? PROCESS_STEPS.indexOf('risk') : currentStepIndex(survey.status);

export type TagColor = 'danger' | 'warning' | 'success' | 'info';

export const LEVEL_COLORS: Record<HmsRiskLevel, TagColor> = {
  high: 'danger',
  moderate: 'warning',
  low: 'success',
};

/** Highest first, as in the list and the legend */
export const RISK_LEVELS: HmsRiskLevel[] = ['high', 'moderate', 'low'];

/** The level the assessment ended with, or the calculated one */
export const assessedLevel = (area: Pick<AssessedArea, 'level' | 'adjustedLevel'>): HmsRiskLevel =>
  area.adjustedLevel ?? area.level;

/** Where the area is in the matrix once the assessment is taken into account */
export const assessedPlacement = (area: AssessedArea) => ({
  consequence: area.adjustment?.consequence ?? area.consequence,
  likelihood: area.adjustment?.likelihood ?? area.likelihood,
  score: area.adjustment?.score ?? area.score,
  level: assessedLevel(area),
});

/** Highest risk first, as the list shows them */
export const byRisk = (areas: AssessedArea[]) =>
  [...areas].sort(
    (a, b) => assessedPlacement(b).score - assessedPlacement(a).score || a.name.localeCompare(b.name, 'nb'),
  );

/** The level of a cell, for instant feedback while the user moves an area */
export const placementLevel = (placement: Placement, levels: { moderateFrom: number; highFrom: number }) =>
  riskLevel(placement.consequence * placement.likelihood, levels);

/** What the process says about the risk step once the survey has closed */
export const riskStepText = (risk: { calculatedAt: string; risk: RiskResult } | null, t: TFunction, language: string) =>
  risk &&
  (risk.risk.status === 'assessed'
    ? t('workEnvironmentProcess.steps.risk.doneText', { date: dayAndMonth(risk.calculatedAt, language) })
    : t('workEnvironmentProcess.steps.risk.insufficientText'));

/** The tag at the top of the page */
export const riskHeadline = (risk: RiskResult | null, t: TFunction): { label: string; color: TagColor } => {
  if (!risk) return { label: t('workEnvironmentRisk.headline.calculating'), color: 'info' };
  if (risk.status === 'insufficientResponses')
    return { label: t('workEnvironmentRisk.headline.insufficient'), color: 'warning' };
  const total = risk.areas.length + risk.unassessed.length;
  const level = RISK_LEVELS.slice(0, 2).find((candidate) =>
    risk.areas.some((area) => assessedLevel(area) === candidate),
  );
  if (!level) return { label: t('workEnvironmentRisk.headline.noneElevated'), color: 'success' };
  const count = risk.areas.filter((area) => assessedLevel(area) === level).length;
  return { label: t(`workEnvironmentRisk.headline.${level}`, { count, total }), color: LEVEL_COLORS[level] };
};

/** The organisation's own areas are marked, so they are not mistaken for the standard */
export const areaName = (area: Pick<RiskArea, 'kind' | 'name'>, t: TFunction) =>
  area.kind === 'custom' ? t('workEnvironmentRisk.area.custom', { name: area.name }) : area.name;

export type MatrixCell = { consequence: number; likelihood: number; level: HmsRiskLevel; count: number };

/** Consequence down the side, highest first, and likelihood along the bottom */
export const matrixCells = (
  areas: AssessedArea[],
  levels: { moderateFrom: number; highFrom: number },
): MatrixCell[][] =>
  [...LEVELS].reverse().map((consequence) =>
    LEVELS.map((likelihood) => ({
      consequence,
      likelihood,
      level: riskLevel(consequence * likelihood, levels),
      count: areas.filter((area) => {
        const placement = assessedPlacement(area);
        return placement.consequence === consequence && placement.likelihood === likelihood;
      }).length,
    })),
  );

export type AreaFilter =
  | { type: 'level'; level: HmsRiskLevel }
  | { type: 'cell'; consequence: number; likelihood: number }
  | null;

const matches = (area: AssessedArea, filter: AreaFilter) => {
  const placement = assessedPlacement(area);
  return (
    !filter ||
    (filter.type === 'level'
      ? placement.level === filter.level
      : placement.consequence === filter.consequence && placement.likelihood === filter.likelihood)
  );
};

export const filteredAreas = (areas: AssessedArea[], filter: AreaFilter) =>
  areas.filter((area) => matches(area, filter));

export const isSameFilter = (a: AreaFilter, b: AreaFilter) => JSON.stringify(a) === JSON.stringify(b);

/** Choosing the same filter again clears it */
export const toggleFilter = (current: AreaFilter, next: NonNullable<AreaFilter>): AreaFilter =>
  isSameFilter(current, next) ? null : next;

export const levelCounts = (areas: AssessedArea[]) =>
  RISK_LEVELS.map((level) => ({ level, count: areas.filter((area) => assessedLevel(area) === level).length }));

export type Agreement = 'same' | 'some' | 'wide';

/** The highest standard deviation for each degree of agreement, on the 1–5 scale */
export const AGREEMENT_LIMITS = { same: 0.8, some: 1.2 };

/**
 * How much the team agrees, from the spread of the answers on a 1–5 scale. Read with one
 * decimal, as it is shown, so «0,8» is always within «up to 0,8». The small addition keeps
 * 0.85 from rounding down because 0.85 × 10 is 8.499999999999998.
 */
export const agreement = (standardDeviation: number): Agreement => {
  const tenths = Math.round(standardDeviation * 10 + 1e-9);
  return tenths <= AGREEMENT_LIMITS.same * 10 ? 'same' : tenths <= AGREEMENT_LIMITS.some * 10 ? 'some' : 'wide';
};

/** «3,6», with the decimal sign of the language */
export const oneDecimal = (value: number, language: string) =>
  new Intl.NumberFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);

const HIGHLIGHTED = 2;

const namesOf = (summary: DimensionSummary | null) =>
  (summary?.areas ?? []).slice(0, HIGHLIGHTED).map((area) => area.name.toLowerCase());

/**
 * The introduction to «Arbeidsmiljøet i tall». High risk is named first, so a good
 * picture of the resources never hides an area that must be followed up.
 */
export const inNumbersIntro = (
  areas: AssessedArea[],
  dimensions: Record<'LOAD' | 'RESOURCE', DimensionSummary | null>,
  t: TFunction,
) => {
  const high = areas.filter((area) => area.level === 'high').length;
  const loads = namesOf(dimensions.LOAD);
  const resources = namesOf(dimensions.RESOURCE);
  return [
    high > 0 && t('workEnvironmentRisk.inNumbers.introHigh', { count: high }),
    loads.length > 0 && t('workEnvironmentRisk.inNumbers.introLoads', { names: joinList(loads, t, 'and') }),
    resources.length > 0 &&
      t('workEnvironmentRisk.inNumbers.introResources', {
        count: resources.length,
        names: joinList(resources, t, 'and'),
      }),
  ]
    .filter(Boolean)
    .join(' ');
};

/** Strains first, then resources, leaving out a dimension without any areas */
export const dimensionColumns = (dimensions: Record<'LOAD' | 'RESOURCE', DimensionSummary | null>) =>
  (['LOAD', 'RESOURCE'] as const).flatMap((dimension) => {
    const summary = dimensions[dimension];
    return summary ? [{ dimension, summary }] : [];
  });

/** The widest possible gap between two means on the 1–5 scale */
export const BALANCE_RANGE = 4;

/**
 * Which side weighs most, as the gap between the means. Means, not sums, so adding
 * an area does not tip the balance on its own.
 */
export const dimensionBalance = (dimensions: Record<'LOAD' | 'RESOURCE', DimensionSummary | null>) =>
  dimensions.LOAD && dimensions.RESOURCE ? dimensions.RESOURCE.average - dimensions.LOAD.average : null;

/** Strains to the left, resources to the right, neutral in the middle */
export const balancePosition = (balance: number) => `${50 + (balance / BALANCE_RANGE) * 50}%`;

/** «+0,4», «−0,4» or «0,0» */
export const signedDecimal = (value: number, language: string) =>
  new Intl.NumberFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  }).format(value);

/** The bar shows a score from 1 to 5 as a share of the width */
export const barPercent = (score: number) => `${((score - 1) / 4) * 100}%`;

export type ActivityEntry =
  | { type: 'calculated'; at: string }
  | { type: 'answered'; responses: number; expected: number; at: string | null }
  | { type: 'created'; at: string }
  | ({ type: 'event' } & RiskActivity);

const newestFirst = (a: ActivityEntry, b: ActivityEntry) => (b.at ?? '').localeCompare(a.at ?? '');

/** Newest first */
export const activityLog = (
  risk: WorkEnvironmentRisk,
  progress: { responses: number; expected: number } | undefined,
  createdAt: string,
): ActivityEntry[] =>
  [
    ...(risk.calculatedAt
      ? [
          { type: 'calculated' as const, at: risk.calculatedAt },
          ...risk.activity.map((event) => ({ type: 'event' as const, ...event })),
        ]
      : []),
    ...(progress
      ? [{ type: 'answered' as const, ...progress, at: risk.calculatedAt ? risk.lastResponseAt : null }]
      : []),
    { type: 'created' as const, at: createdAt },
  ].sort(newestFirst);

export const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/** «I dag kl. 23:37» or «21. september kl. 10:00» */
export const dayAndTime = (value: string, t: TFunction, language: string, now = new Date()) => {
  const time = new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
  return sameDay(new Date(value), now)
    ? t('workEnvironmentRisk.activity.today', { time })
    : t('workEnvironmentRisk.activity.dayAndTime', { date: dayAndMonth(value, language), time });
};
